/**
 * MAMAVIBEZ SHOPIFY THEME - PREDICTIVE SEARCH ENGINE
 */

const safeDebounceSearch = (fn, wait) => {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
};

function formatSearchPrice(price) {
  if (!price && price !== 0) return '';
  if (typeof price === 'string' && (price.includes('₹') || price.includes('$') || price.includes('£'))) {
    return price;
  }
  if (typeof window.formatMoney === 'function') {
    try {
      return window.formatMoney(price);
    } catch (e) {}
  }
  return `₹${price}`;
}

function escapeSearchHTML(str) {
  if (!str) return '';
  return String(str).replace(/[&<>'"]/g, 
    tag => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    }[tag] || tag)
  );
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.modal = this.closest('.predictive-search-modal');
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.cachedResults = {};
    this.initEvents();
  }

  initEvents() {
    const debouncedChange = typeof debounce === 'function' ? debounce((e) => this.onChange(e), 300) : safeDebounceSearch((e) => this.onChange(e), 300);
    this.input?.addEventListener('input', debouncedChange);

    // Keyboard navigation
    this.addEventListener('keydown', this.onKeyDown.bind(this));

    // Form submit handler
    this.form?.addEventListener('submit', (e) => {
      const term = this.input.value.trim();
      if (!term.length) {
        e.preventDefault();
      }
    });
  }

  open() {
    this.modal?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => {
      this.input?.focus();
      this.input?.select();
    }, 120);
  }

  close() {
    this.modal?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.clearResults();
      return;
    }

    if (this.cachedResults[searchTerm]) {
      this.renderResults(this.cachedResults[searchTerm], searchTerm);
      return;
    }

    try {
      const searchUrl = window.routes?.predictive_search_url || '/search/suggest';
      const url = `${searchUrl}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,page&resources[limit]=6`;
      const res = await fetch(url);
      
      if (!res.ok) {
        this.renderFallbackSearch(searchTerm);
        return;
      }

      const data = await res.json();
      const results = data.resources?.results || data.results || {};
      this.cachedResults[searchTerm] = results;
      this.renderResults(results, searchTerm);
    } catch (err) {
      console.warn('Predictive search fallback triggered:', err);
      this.renderFallbackSearch(searchTerm);
    }
  }

  renderFallbackSearch(term) {
    if (!this.resultsContainer) return;
    this.resultsContainer.innerHTML = `
      <div class="predictive-search-view-all" style="padding: 2.5rem 0; text-align: center;">
        <p style="margin-bottom: 1.5rem; font-size: 1.5rem; color: #555;">Search for "<strong>${escapeSearchHTML(term)}</strong>"</p>
        <a href="/search?q=${encodeURIComponent(term)}" class="button button--primary button--pill">
          View All Matching Products &rarr;
        </a>
      </div>
    `;
  }

  renderResults(results, term) {
    if (!this.resultsContainer) return;

    const products = results.products || [];
    const collections = results.collections || [];

    if (!products.length && !collections.length) {
      this.resultsContainer.innerHTML = `
        <div class="predictive-search-empty text-center" style="padding: 3rem 0; text-align: center;">
          <p style="font-size: 1.5rem; color: #666; margin-bottom: 1.5rem;">No direct suggestions for "<strong>${escapeSearchHTML(term)}</strong>".</p>
          <a href="/search?q=${encodeURIComponent(term)}" class="button button--secondary button--pill">
            Search full store for "${escapeSearchHTML(term)}"
          </a>
        </div>
      `;
      return;
    }

    let html = '<div class="predictive-search-results__grid">';

    // Products Column
    if (products.length) {
      html += `
        <div class="predictive-search-column">
          <h4 class="predictive-search-results__heading">Products (${products.length})</h4>
          <div class="predictive-search-list">
            ${products.map((p) => {
              let imgUrl = '';
              if (typeof p.featured_image === 'string') {
                imgUrl = p.featured_image;
              } else if (p.featured_image && p.featured_image.url) {
                imgUrl = p.featured_image.url;
              } else if (p.image) {
                imgUrl = typeof p.image === 'string' ? p.image : (p.image.url || '');
              }

              return `
                <a href="${p.url}" class="predictive-search-item">
                  <div class="predictive-search-item__media">
                    ${imgUrl ? `<img src="${imgUrl}" alt="${escapeSearchHTML(p.title)}" width="55" height="70" loading="lazy">` : `<div style="background:#F0ECE8; width:100%; height:100%; display:flex; align-items:center; justify-content:center; color:#AAA;">✨</div>`}
                  </div>
                  <div class="predictive-search-item__info">
                    <span class="predictive-search-item__title">${escapeSearchHTML(p.title)}</span>
                    <span class="predictive-search-item__price">${formatSearchPrice(p.price)}</span>
                  </div>
                </a>
              `;
            }).join('')}
          </div>
        </div>
      `;
    }

    // Collections Column
    if (collections.length) {
      html += `
        <div class="predictive-search-column">
          <h4 class="predictive-search-results__heading">Categories</h4>
          <div class="predictive-search-list">
            ${collections.map((c) => `
              <a href="${c.url}" class="predictive-search-item">
                <div class="predictive-search-item__info">
                  <span class="predictive-search-item__title">📂 ${escapeSearchHTML(c.title)}</span>
                </div>
              </a>
            `).join('')}
          </div>
        </div>
      `;
    }

    html += '</div>';

    // View All Link
    html += `
      <div class="predictive-search-view-all" style="margin-top: 2.5rem; text-align: center;">
        <a href="/search?q=${encodeURIComponent(term)}" class="button button--secondary button--pill">
          View all results for "${escapeSearchHTML(term)}" &rarr;
        </a>
      </div>
    `;

    this.resultsContainer.innerHTML = html;
  }

  clearResults() {
    if (this.resultsContainer) this.resultsContainer.innerHTML = '';
  }

  onKeyDown(e) {
    if (e.key === 'Escape') {
      this.close();
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}

// Global click event delegation for search modal triggers & closers
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-search-trigger]')) {
    e.preventDefault();
    const searchEl = document.querySelector('predictive-search');
    if (searchEl && typeof searchEl.open === 'function') {
      searchEl.open();
    }
    return;
  }
  if (e.target.closest('[data-search-close]')) {
    e.preventDefault();
    const searchEl = document.querySelector('predictive-search');
    if (searchEl && typeof searchEl.close === 'function') {
      searchEl.close();
    }
    return;
  }
});
